import React from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { Box, Skeleton } from '@mui/material';
import ProductCard from './ProductCard';
import DragHandle from './DragHandle';
import ReorderControls from './ReorderControls';
import ProductActions from './ProductActions';
import gridStyles from '../styles/BrandProductsGrid.module.scss';

export default function BrandProductsGrid({
    items = [],
    loading = false,
    savingOrder = false,
    selectedIds = new Set(),
    onToggleSelect,
    onSelectAll,
    onDelete,
    onDragEnd,
    onMove,
    brand,
}) {
    const allSelected = Boolean(items.length) && items.every((item) => selectedIds.has(item.id));

    if (loading) {
        return (
            <div className={gridStyles.list}>
                {Array.from({ length: 8 }).map((_, idx) => (
                    <Skeleton key={idx} variant="rounded" height={76} />
                ))}
            </div>
        );
    }

    if (!items.length) {
        return (
            <div className={gridStyles.emptyState}>
                <h3>No products in this brand yet</h3>
                <p>No products in this brand yet. Click "Add product" to start.</p>
            </div>
        );
    }

    return (
        <>
            <div className={gridStyles.bulkBar}>
                <label className={gridStyles.bulkCheck}>
                    <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={(e) => onSelectAll?.(e.target.checked)}
                    />
                    <span>Select all</span>
                </label>
                <span>{selectedIds.size} selected</span>
            </div>

            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="brand-products">
                    {(dropProvided, dropSnapshot) => (
                        <div
                            ref={dropProvided.innerRef}
                            {...dropProvided.droppableProps}
                            className={gridStyles.list}
                        >
                            {items.map((product, index) => (
                                <Draggable
                                    key={product.id}
                                    draggableId={String(product.id)}
                                    index={index}
                                    isDragDisabled={savingOrder}
                                >
                                    {(dragProvided) => (
                                        <div
                                            ref={dragProvided.innerRef}
                                            {...dragProvided.draggableProps}
                                            className={gridStyles.rowWrap}
                                            style={{
                                                ...dragProvided.draggableProps.style,
                                                transition: 'transform 180ms ease',
                                            }}
                                        >
                                            <ProductCard
                                                product={product}
                                                brand={brand}
                                                showSelection
                                                selected={selectedIds.has(product.id)}
                                                onSelect={(checked) => onToggleSelect?.(product.id, checked)}
                                                onKeyDown={(event) => {
                                                    if (!event.altKey || savingOrder) return;
                                                    if (event.key === 'ArrowUp') {
                                                        event.preventDefault();
                                                        onMove?.(index, index - 1);
                                                    }
                                                    if (event.key === 'ArrowDown') {
                                                        event.preventDefault();
                                                        onMove?.(index, index + 1);
                                                    }
                                                }}
                                                reorderSlot={(
                                                    <>
                                                        <DragHandle
                                                            dragHandleProps={dragProvided.dragHandleProps}
                                                            disabled={savingOrder}
                                                            label={`Drag ${product.name || 'product'} to reorder`}
                                                        />
                                                        <ReorderControls
                                                            disabled={savingOrder}
                                                            canMoveUp={index > 0}
                                                            canMoveDown={index < items.length - 1}
                                                            onMoveUp={() => onMove?.(index, index - 1)}
                                                            onMoveDown={() => onMove?.(index, index + 1)}
                                                            onMoveTop={() => onMove?.(index, 0)}
                                                            onMoveBottom={() => onMove?.(index, items.length - 1)}
                                                        />
                                                    </>
                                                )}
                                                actionsSlot={(
                                                    <ProductActions
                                                        editTo={`/products/${product.id}`}
                                                        onDelete={() => onDelete?.(product.id)}
                                                    />
                                                )}
                                            />
                                        </div>
                                    )}
                                </Draggable>
                            ))}

                            {dropSnapshot.isDraggingOver && (
                                <Box className={gridStyles.placeholder}>Drop here to reorder</Box>
                            )}

                            {dropProvided.placeholder}
                        </div>
                    )}
                </Droppable>
            </DragDropContext>
        </>
    );
}
